import { useState } from 'react';
import { useSeoMeta } from '@unhead/react';
import { Bitcoin, MapPin, Users, Calendar, Zap, ChevronDown, BookOpen, ArrowRight, Menu, X } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { MeetupCard } from '@/components/MeetupCard';
import { useCalendarEvents } from '@/hooks/useCalendarEvents';
import { useBlogPosts } from '@/hooks/useBlogPosts';
import { LoginArea } from '@/components/auth/LoginArea';
import { nip19 } from 'nostr-tools';

// Uploaded images
const LOGO_URL = 'https://blossom.ditto.pub/e5ad3fc200840da5f830b1a978f1660e5e5f7ccce36df334f519fbbb90d3f0a6.png';
const BANNER_URL = 'https://blossom.ditto.pub/e5ad3fc200840da5f830b1a978f1660e5e5f7ccce36df334f519fbbb90d3f0a6.png';

function MeetupSkeleton() {
  return (
    <Card className="border-white/10 bg-[#0a0a0a] overflow-hidden">
      <Skeleton className="h-36 sm:h-44 w-full rounded-none bg-white/5" />
      <div className="p-4 sm:p-5 space-y-3">
        <Skeleton className="h-5 w-3/4 bg-white/5" />
        <Skeleton className="h-4 w-1/2 bg-white/5" />
        <Skeleton className="h-4 w-2/3 bg-white/5" />
        <Skeleton className="h-4 w-full bg-white/5" />
        <Skeleton className="h-9 w-full mt-2 bg-white/5" />
      </div>
    </Card>
  );
}

function BlogPostSkeleton() {
  return (
    <Card className="border-white/10 bg-[#0a0a0a] overflow-hidden">
      <Skeleton className="h-32 sm:h-36 w-full rounded-none bg-white/5" />
      <div className="p-4 space-y-2">
        <Skeleton className="h-4 w-3/4 bg-white/5" />
        <Skeleton className="h-3 w-full bg-white/5" />
        <Skeleton className="h-3 w-2/3 bg-white/5" />
      </div>
    </Card>
  );
}

const Index = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useSeoMeta({
    title: '951 Bitcoin — Bitcoin-Only Meetup Group in Riverside County, California',
    description: 'Join 951 Bitcoin, a Bitcoin-only meetup group in Riverside County, California. We promote Bitcoin as an electronic cash system and meet only at venues that accept Bitcoin payments.',
  });

  const { data, isLoading, isError } = useCalendarEvents();
  const { data: blogPosts, isLoading: isBlogLoading } = useBlogPosts();

  const upcomingEvents = data?.upcoming ?? [];
  const pastEvents = data?.past ?? [];
  const totalEvents = (data?.all ?? []).length;

  const closeMobileMenu = () => setMobileMenuOpen(false);

  return (
    <div className="min-h-screen bg-[#080808] text-white overflow-x-hidden">

      {/* ── NAV ── */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-md border-b border-white/5">
        <div className="max-w-6xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3">
            <img
              src={LOGO_URL}
              alt="951 Bitcoin Logo"
              className="h-7 w-7 sm:h-8 sm:w-8 rounded-full object-cover"
            />
            <span className="font-bold text-white text-base sm:text-lg tracking-tight">951 Bitcoin</span>
          </div>
          <div className="flex items-center gap-3 sm:gap-4">
            {/* Desktop nav links */}
            <a href="#meetups" className="text-sm text-white/60 hover:text-white transition-colors hidden md:block">
              Meetups
            </a>
            <a href="#blog" className="text-sm text-white/60 hover:text-white transition-colors hidden md:block">
              Blog
            </a>
            <a href="#about" className="text-sm text-white/60 hover:text-white transition-colors hidden md:block">
              About
            </a>
            <div className="hidden sm:block">
              <LoginArea className="max-w-48" />
            </div>
            {/* Mobile hamburger */}
            <button
              className="md:hidden p-1.5 rounded-lg text-white/60 hover:text-white hover:bg-white/10 transition-colors"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {/* Mobile menu dropdown */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-black/95 backdrop-blur-lg border-b border-white/5 animate-fade-in-up">
            <div className="px-4 py-4 space-y-1">
              <a
                href="#meetups"
                className="block py-3 px-3 text-white/80 hover:text-white hover:bg-white/5 rounded-lg transition-colors text-base"
                onClick={closeMobileMenu}
              >
                Meetups
              </a>
              <a
                href="#blog"
                className="block py-3 px-3 text-white/80 hover:text-white hover:bg-white/5 rounded-lg transition-colors text-base"
                onClick={closeMobileMenu}
              >
                Blog
              </a>
              <a
                href="#about"
                className="block py-3 px-3 text-white/80 hover:text-white hover:bg-white/5 rounded-lg transition-colors text-base"
                onClick={closeMobileMenu}
              >
                About
              </a>
              <div className="pt-2 px-3">
                <LoginArea className="w-full" />
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* ── HERO ── */}
      <section className="relative min-h-[90vh] sm:min-h-screen flex flex-col items-center justify-center overflow-hidden pt-14">
        {/* Banner background */}
        <div className="absolute inset-0 isolate">
          <img
            src={BANNER_URL}
            alt="951 Bitcoin Banner"
            className="w-full h-full object-contain opacity-10"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/60 to-[#080808]" />
          {/* Purple glow orbs - scaled down on mobile */}
          <div className="absolute top-1/3 left-1/4 w-48 sm:w-96 h-48 sm:h-96 bg-[#7B2FBE]/10 rounded-full blur-3xl -z-10" />
          <div className="absolute bottom-1/3 right-1/4 w-40 sm:w-80 h-40 sm:h-80 bg-[#7B2FBE]/8 rounded-full blur-3xl -z-10" />
        </div>

        <div className="relative z-10 flex flex-col items-center text-center px-5 sm:px-4 max-w-4xl mx-auto space-y-5 sm:space-y-8">
          {/* Logo */}
          <div className="animate-fade-in-up">
            <div className="relative inline-block">
              <div className="absolute inset-0 bg-[#7B2FBE]/20 rounded-2xl blur-xl" />
              <img
                src={LOGO_URL}
                alt="951 Bitcoin"
                className="relative w-28 h-28 sm:w-40 sm:h-40 rounded-2xl object-contain shadow-2xl border border-[#7B2FBE]/30"
              />
            </div>
          </div>

          {/* Title */}
          <div className="animate-fade-in-up-delay-1 space-y-1.5 sm:space-y-2">
            <h1 className="text-4xl sm:text-5xl md:text-7xl font-black tracking-tight text-white">
              951 <span className="text-[#7B2FBE] glow-purple-text">Bitcoin</span>
            </h1>
            <div className="flex items-center justify-center gap-1.5 sm:gap-2 text-white/50 text-xs sm:text-sm font-medium tracking-widest uppercase">
              <MapPin className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-[#7B2FBE]" />
              <span>Riverside County, California</span>
            </div>
          </div>

          {/* Welcome paragraph */}
          <div className="animate-fade-in-up-delay-2 max-w-2xl">
            <p className="text-base sm:text-lg md:text-xl text-white/75 leading-relaxed">
              Welcome to <strong className="text-white">951 Bitcoin</strong>,{' '}
              <strong className="text-[#7B2FBE]">a Bitcoin-Only Meetup Group in Riverside County, California</strong>.
              Join us as we promote the use of Bitcoin as an electronic cash system.{' '}
              <strong className="text-white">Meetings will only be held at places that accept Bitcoin for payments</strong>.
            </p>
          </div>

          {/* Stats row */}
          <div className="animate-fade-in-up-delay-3 flex items-center gap-5 sm:gap-10">
            <div className="flex flex-col items-center gap-1">
              <span className="text-2xl sm:text-3xl font-black text-[#7B2FBE]">
                {isLoading ? '—' : totalEvents}
              </span>
              <span className="text-[10px] sm:text-xs text-white/40 uppercase tracking-widest">Total Meetups</span>
            </div>
            <div className="w-px h-8 sm:h-10 bg-white/10" />
            <div className="flex flex-col items-center gap-1">
              <span className="text-2xl sm:text-3xl font-black text-[#7B2FBE]">
                {isLoading ? '—' : upcomingEvents.length}
              </span>
              <span className="text-[10px] sm:text-xs text-white/40 uppercase tracking-widest">Upcoming</span>
            </div>
            <div className="w-px h-8 sm:h-10 bg-white/10" />
            <div className="flex flex-col items-center gap-1">
              <Bitcoin className="h-6 w-6 sm:h-8 sm:w-8 text-[#7B2FBE]" />
              <span className="text-[10px] sm:text-xs text-white/40 uppercase tracking-widest">Only</span>
            </div>
          </div>

          {/* Scroll hint */}
          <a
            href="#meetups"
            className="animate-fade-in-up-delay-3 flex flex-col items-center gap-1.5 sm:gap-2 text-white/30 hover:text-[#7B2FBE] transition-colors mt-2 sm:mt-4"
          >
            <span className="text-[10px] sm:text-xs uppercase tracking-widest">See Meetups</span>
            <ChevronDown className="h-4 w-4 sm:h-5 sm:w-5 animate-bounce" />
          </a>
        </div>
      </section>

      {/* ── ABOUT ── */}
      <section id="about" className="py-12 sm:py-20 px-4 bg-[#080808]">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
            {[
              {
                icon: <Bitcoin className="h-6 w-6 sm:h-7 sm:w-7 text-[#7B2FBE]" />,
                title: 'Bitcoin-Only',
                desc: 'We are focused exclusively on Bitcoin as a peer-to-peer electronic cash system, just as Satoshi intended.',
              },
              {
                icon: <MapPin className="h-6 w-6 sm:h-7 sm:w-7 text-[#7B2FBE]" />,
                title: 'Bitcoin-Accepting Venues',
                desc: 'Every meetup is held at a local business in Riverside County, CA that accepts Bitcoin as payment — putting BTC to work.',
              },
              {
                icon: <Users className="h-6 w-6 sm:h-7 sm:w-7 text-[#7B2FBE]" />,
                title: 'Community First',
                desc: 'A welcoming gathering for bitcoiners of all experience levels across the 951 area code in the Inland Empire.',
              },
            ].map((item) => (
              <Card
                key={item.title}
                className="bg-[#0d0d0d] border-white/8 hover:border-[#7B2FBE]/30 transition-all duration-300 group"
              >
                <CardContent className="pt-5 pb-5 sm:pt-6 sm:pb-6 space-y-2.5 sm:space-y-3">
                  <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-xl bg-[#7B2FBE]/10 flex items-center justify-center group-hover:bg-[#7B2FBE]/20 transition-colors duration-300">
                    {item.icon}
                  </div>
                  <h3 className="font-bold text-white text-base sm:text-lg">{item.title}</h3>
                  <p className="text-white/55 text-sm leading-relaxed">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ── MEETUPS FEED ── */}
      <section id="meetups" className="py-12 sm:py-20 px-4 bg-[#050505]">
        <div className="max-w-6xl mx-auto">

          {/* Section header */}
          <div className="mb-8 sm:mb-12 flex flex-col sm:flex-row sm:items-end justify-between gap-3 sm:gap-4">
            <div>
              <div className="flex items-center gap-2 mb-1.5 sm:mb-2">
                <div className="h-px w-6 sm:w-8 bg-[#7B2FBE]" />
                <span className="text-[#7B2FBE] text-[10px] sm:text-xs font-bold uppercase tracking-widest">NIP-52 Calendar Events</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-black text-white">Meetups</h2>
            </div>
            <div className="flex items-center gap-2 text-white/40 text-xs sm:text-sm">
              <Zap className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-[#7B2FBE]" />
              <span>Powered by Nostr</span>
            </div>
          </div>

          {/* Upcoming */}
          <div className="mb-10 sm:mb-14">
            <h3 className="text-lg sm:text-xl font-bold text-white mb-4 sm:mb-6 flex items-center gap-2 sm:gap-3">
              <Calendar className="h-4 w-4 sm:h-5 sm:w-5 text-[#7B2FBE]" />
              Upcoming Events
              {!isLoading && upcomingEvents.length > 0 && (
                <span className="text-sm font-normal text-white/40">({upcomingEvents.length})</span>
              )}
            </h3>

            {isLoading && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                {[1, 2, 3].map((i) => <MeetupSkeleton key={i} />)}
              </div>
            )}

            {!isLoading && isError && (
              <Card className="border-dashed border-white/10 bg-transparent">
                <CardContent className="py-10 sm:py-12 text-center">
                  <p className="text-white/40 text-sm">
                    Could not load meetups. Check your relay connections and try again.
                  </p>
                </CardContent>
              </Card>
            )}

            {!isLoading && !isError && upcomingEvents.length === 0 && (
              <Card className="border-dashed border-[#7B2FBE]/20 bg-[#7B2FBE]/5">
                <CardContent className="py-10 sm:py-12 text-center space-y-2">
                  <Calendar className="h-8 w-8 sm:h-10 sm:w-10 text-[#7B2FBE]/40 mx-auto" />
                  <p className="text-white/50 text-sm">No upcoming meetups yet. Check back soon!</p>
                  <p className="text-white/30 text-xs">
                    Events are published on Nostr via NIP-52 (kinds 31922 &amp; 31923)
                  </p>
                </CardContent>
              </Card>
            )}

            {!isLoading && !isError && upcomingEvents.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                {upcomingEvents.map((event, i) => (
                  <MeetupCard key={event.id} event={event} featured={i === 0} />
                ))}
              </div>
            )}
          </div>

          {/* Past events */}
          {!isLoading && pastEvents.length > 0 && (
            <div>
              <h3 className="text-lg sm:text-xl font-bold text-white/60 mb-4 sm:mb-6 flex items-center gap-2 sm:gap-3">
                <Calendar className="h-4 w-4 sm:h-5 sm:w-5 text-white/30" />
                Past Events
                <span className="text-sm font-normal text-white/30">({pastEvents.length})</span>
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                {pastEvents.map((event) => (
                  <MeetupCard key={event.id} event={event} />
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ── BLOG ── */}
      <section id="blog" className="py-12 sm:py-20 px-4 bg-[#080808]">
        <div className="max-w-6xl mx-auto">

          {/* Section header */}
          <div className="mb-8 sm:mb-12 flex flex-col sm:flex-row sm:items-end justify-between gap-3 sm:gap-4">
            <div>
              <div className="flex items-center gap-2 mb-1.5 sm:mb-2">
                <div className="h-px w-6 sm:w-8 bg-[#7B2FBE]" />
                <span className="text-[#7B2FBE] text-[10px] sm:text-xs font-bold uppercase tracking-widest">NIP-23 Long-form Articles</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-black text-white">Blog</h2>
            </div>
            <a
              href="https://habla.news/p/npub195l6tcfe03d8fmkyfj3ra6lnsesta9d2jdphcx4ymgsn59kwfyms6nvvre"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-[#7B2FBE] hover:text-white text-sm transition-colors group"
            >
              <span>View all posts</span>
              <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </a>
          </div>

          {/* Blog loading skeletons */}
          {isBlogLoading && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {[1, 2, 3].map((i) => <BlogPostSkeleton key={i} />)}
            </div>
          )}

          {/* Blog posts grid */}
          {!isBlogLoading && blogPosts && blogPosts.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {blogPosts.slice(0, 6).map((post) => {
                const naddr = nip19.naddrEncode({
                  kind: 30023,
                  pubkey: post.pubkey,
                  identifier: post.slug,
                });
                const postDate = new Date(post.publishedAt * 1000).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                });
                return (
                  <a
                    key={post.id}
                    href={`https://habla.news/a/${naddr}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group block"
                  >
                    <Card className="h-full border-white/10 bg-[#0a0a0a] hover:border-[#7B2FBE]/50 hover:shadow-md hover:shadow-[#7B2FBE]/10 active:scale-[0.98] transition-all duration-300 overflow-hidden">
                      {/* Top accent bar */}
                      <div className="h-0.5 bg-transparent group-hover:bg-[#7B2FBE]/50 transition-all duration-300" />

                      {/* Cover image */}
                      {post.image ? (
                        <div className="relative overflow-hidden h-32 sm:h-36 w-full">
                          <img
                            src={post.image}
                            alt={post.title}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-transparent to-transparent" />
                        </div>
                      ) : (
                        <div className="h-32 sm:h-36 w-full bg-gradient-to-br from-[#7B2FBE]/10 to-[#0a0a0a] flex items-center justify-center">
                          <BookOpen className="h-8 w-8 sm:h-10 sm:w-10 text-[#7B2FBE]/30" />
                        </div>
                      )}

                      <CardContent className="p-3.5 sm:p-4 space-y-1.5 sm:space-y-2">
                        <p className="text-xs text-white/35">{postDate}</p>
                        <h3 className="font-bold text-white text-sm sm:text-base leading-snug line-clamp-2 group-hover:text-[#7B2FBE] transition-colors duration-200">
                          {post.title}
                        </h3>
                        {post.summary && (
                          <p className="text-xs sm:text-sm text-white/50 line-clamp-2 leading-relaxed">
                            {post.summary}
                          </p>
                        )}
                        {post.hashtags.length > 0 && (
                          <div className="flex flex-wrap gap-1 pt-1">
                            {post.hashtags.slice(0, 3).map((tag) => (
                              <Badge
                                key={tag}
                                variant="outline"
                                className="text-[10px] sm:text-xs border-[#7B2FBE]/20 text-[#7B2FBE]/70 px-1.5 py-0"
                              >
                                #{tag}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </a>
                );
              })}
            </div>
          )}

          {/* Empty state */}
          {!isBlogLoading && (!blogPosts || blogPosts.length === 0) && (
            <Card className="border-dashed border-[#7B2FBE]/20 bg-[#7B2FBE]/5">
              <CardContent className="py-10 sm:py-12 text-center space-y-2">
                <BookOpen className="h-8 w-8 sm:h-10 sm:w-10 text-[#7B2FBE]/40 mx-auto" />
                <p className="text-white/50 text-sm">No blog posts found yet.</p>
                <p className="text-white/30 text-xs">
                  Articles are published on Nostr via NIP-23 (kind 30023)
                </p>
              </CardContent>
            </Card>
          )}

          {/* View all link at bottom */}
          {!isBlogLoading && blogPosts && blogPosts.length > 0 && (
            <div className="mt-6 sm:mt-8 text-center">
              <a
                href="https://habla.news/p/npub195l6tcfe03d8fmkyfj3ra6lnsesta9d2jdphcx4ymgsn59kwfyms6nvvre"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-5 sm:px-6 py-2.5 rounded-full border border-[#7B2FBE]/30 text-[#7B2FBE] hover:bg-[#7B2FBE] hover:text-white hover:border-[#7B2FBE] active:scale-95 transition-all duration-200 text-sm font-medium"
              >
                <BookOpen className="h-4 w-4" />
                Read All Articles on Habla.news
                <ArrowRight className="h-4 w-4" />
              </a>
            </div>
          )}
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="border-t border-white/5 py-8 sm:py-10 px-4 bg-black">
        <div className="max-w-6xl mx-auto flex flex-col items-center gap-3 sm:gap-4 text-xs sm:text-sm text-white/30 text-center">
          <div className="flex items-center gap-2 sm:gap-3">
            <img src={LOGO_URL} alt="951 Bitcoin" className="h-5 w-5 sm:h-6 sm:w-6 rounded-full object-cover" />
            <span className="font-semibold text-white/50">951 Bitcoin</span>
            <span>· Riverside County, California</span>
          </div>
          <div className="flex items-center gap-1 flex-wrap justify-center">
            <Bitcoin className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-[#7B2FBE]" />
            <span>Bitcoin-Only Meetup · Built on Nostr ·{' '}</span>
            <a
              href="https://shakespeare.diy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#7B2FBE]/60 hover:text-[#7B2FBE] transition-colors"
            >
              Vibed with Shakespeare
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Index;
